import {
    LayoutGrid,
    Menu,
    LayoutDashboard,
    Calendar,
    BookOpen,
    ShoppingCart,
    Globe,
    Users,
    Settings,
    type LucideIcon,
    ArrowLeftRight,
    ClipboardList,
    CreditCard,
    BarChart3,
    Image as ImageIcon
} from "lucide-react";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";

interface SidebarItem {
    id: string;
    label: string;
    icon:  string | LucideIcon;
    path: string;
}

const mainItems: SidebarItem[] = [
    {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/aesthetics/dashboard",
    },
    {
        id: "calendar",
        label: "Calendar",
        icon: Calendar,
        path: "/aesthetics/calendar",
    },
    {
        id: "bookings",
        label: "Bookings",
        icon: BookOpen,
        path: "/aesthetics/bookings",
    },
    { id: "pos", label: "POS", icon: ShoppingCart, path: "/aesthetics/pos" },
    {
        id: "website",
        label: "Website",
        icon: Globe,
        path: "/aesthetics/website",
    },
    { id: "manage", label: "Manage", icon: LayoutGrid, path: "/aesthetics/manage" },
    {
        id: "clients",
        label: "Clients",
        icon: Users,
        path: "/aesthetics/clients",
    },
    {
        id: "treatments",
        label: "Treatments",
        icon: ClipboardList,
        path: "/aesthetics/treatments",
    },
    {
        id: "gallery",           
        label: "Gallery",        
        icon: ImageIcon,            
        path: "/aesthetics/gallery",  
    },
    {
        id: "payments",          
        label: "Payments",       
        icon: CreditCard,       
        path: "/aesthetics/payments", 
    },
    // {
    //     id: "consultation",
    //     label: "Consultation",
    //     icon: MessageSquareText,
    //     path: "/aesthetics/consultation",
    // },
    // {
    //     id: "sunbeds",
    //     label: "Sunbeds",
    //     icon: Sun,
    //     path: "/aesthetics/sunbeds",
    // },
    // {
    //     id: "sunbed-session",
    //     label: "Sunbed Session",
    //     icon: Timer,
    //     path: "/aesthetics/sunbed-session",
    // },
    {
        id: "reports",
        label: "Reports",
        icon: BarChart3,
        path: "/aesthetics/reports",
    },
];

const footerItem: SidebarItem = {
    id: "settings",
    label: "Settings",
    icon: Settings,
    path: "/aesthetics/settings",
};
interface AestheticsSidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
}

const AestheticsSidebar: React.FC<AestheticsSidebarProps> = ({ isOpen = false, onClose }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const toggleSidebar = () => setIsExpanded(!isExpanded);

    const renderIcon = (item: SidebarItem, isActive: boolean) => {
        const IconComponent = item.icon;

        return (
            <div
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-200
          ${isActive ? "bg-[#764BB7] shadow-md" : "bg-[#EDEDF5] hover:bg-[#764BB7]/15"}`}
            >
                <IconComponent
                    size={20}
                    className={isActive ? "text-white" : "text-[#764BB7]"}
                />
            </div>
        );
    };
    const renderExpandedIcon = (item: SidebarItem, isActive: boolean) => {
        const IconComponent = item.icon;

        return (
            <div
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-200`}
            >
                <IconComponent
                    size={20}
                    className={isActive ? "text-white" : "text-white"}
                />
            </div>
        );
    };

    const renderRailIcon = (item: SidebarItem) => (
        <NavLink
            key={item.id}
            to={item.path}
            className="flex items-center justify-center py-1.5 group"
            title={item.label}
        >
            {({ isActive }) => renderIcon(item, isActive)}
        </NavLink>
    );

    const renderExpandedNavLink = (item: SidebarItem) => (
        <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl rounded-l-none cursor-pointer transition-all duration-200
          ${isActive
                    ? "bg-[#764BB7] font-medium bg-white/10"
                    : "text-sidebar-foreground/80 hover:bg-white/10 hover:text-sidebar-foreground"
                }`
            }
        >
            {({ isActive }) => (
                <>
                    {renderExpandedIcon(item, isActive)}
                    <span className="text-sm whitespace-nowrap">{item.label}</span>
                </>
            )}
        </NavLink>
    );

    return <>
        <div className="flex h-screen bg-xx">
            {/* Icon Rail - Always visible */}
            <div
                className="w-16 h-screen flex flex-col items-center py-4 shrink-0"
                style={{
                    background: "linear-gradient(180deg, hsl(var(--sidebar-background)) 0%, hsl(var(--sidebar-background-end)) 100%)",
                }}
            >
                {/* Burger Menu Button */}
                <button
                    onClick={toggleSidebar}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#EDEDF5] text-[#764BB7]  transition-colors duration-200 mb-6 hover:bg-[#764BB7]/15"
                    aria-label={isExpanded ? "Close sidebar" : "Open sidebar"}
                >
                    <Menu size={20} className="text-sidebar-foreground" />
                </button>

                {/* Icon Rail Navigation */}
                <nav className="flex-1 flex flex-col gap-1 overflow-y-auto scrollbar-hidden">
                    {mainItems.map(renderRailIcon)}
                </nav>

                {/* Footer Icon */}
                
                <div className="pt-4 border-t border-white/20 w-full flex justify-center">
                    {renderRailIcon(footerItem)}
                </div>
            </div>

            {/* Expanded Panel - Conditionally visible */}
            <div
                className={`text-white h-screen flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "w-56 opacity-100" : "w-0 opacity-0"
                    }`}
                style={{
                    background: "#704AAF",
                }}
            >
                {/* Logo Header */}
                <div className="px-4 py-5  bg-[#4E3183]">
                    
                    <img src="/logo.svg" alt="Glossa Aesthetics" className="h-8 lg:h-9 w-auto"/>
                </div>

                {/* Expanded Navigation */}
                <nav className="flex-1 overflow-y-auto p-3 ps-0 space-y-1 scrollbar-hidden">
                    {[...mainItems, footerItem].map(renderExpandedNavLink)}
                </nav>
            </div>

            {/* Mobile Overlay */}
            {isExpanded && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={toggleSidebar}
                    style={{ left: "calc(4rem + 14rem)" }}
                />
            )}
        </div>
    </>
};

export default AestheticsSidebar;